import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IntakeFormData } from '../../lib/types/IntakeFormTypes';
// import IntakeFormReadOnly from '../common/IntakeFormReadOnly';
import { ClientContentLibrary } from './ClientContentLibrary';
import { AppointmentFlow } from '../client-portal/appointments/AppointmentFlow';
import { AppointmentTypeCard } from '../client-portal/appointments/AppointmentTypeCard';
import { useAuth } from "../../hooks/useSupabaseAuth"
import { API_ENDPOINTS, getAuthHeaders, shouldIncludeCredentials } from '../../lib/config/api-endpoints';

// interface Comment {
//   text: string;
//   author: string;
//   timestamp: string;
// }

interface ClientBooking {
  id: number;
  product_id: string;
  time_slot: string;
  status: string;
  amount: number;
  currency: string;
  stripe_session_id: string | null;
  payment_intent_id: string | null;
  calendar_event_id: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface CategorizedBookings {
  upcoming: ClientBooking[];
  past: ClientBooking[];
  cancelled: ClientBooking[];
}

const ClientPortal: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { client, isLoading: authLoading, isAuthenticated, logout } = useAuth();
  const [clientData, setClientData] = useState<IntakeFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // const [comments, setComments] = useState<Comment[]>([]);
  const [bookings, setBookings] = useState<CategorizedBookings>({ upcoming: [], past: [], cancelled: [] });
  // const [isEditMode, setIsEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'appointments' | 'resources'>('appointments');
  const [eligibility, setEligibility] = useState<any[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<any[]>([]);

  // const fetchComments = useCallback(async (clientId: string) => {
  //   // Comments disabled for now
  //   setComments([]);
  // }, []);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.client.bookings, {
        headers: getAuthHeaders(API_ENDPOINTS.client.bookings),
        credentials: shouldIncludeCredentials(API_ENDPOINTS.client.bookings) ? 'include' : 'same-origin'
      });
      
      if (response.ok) {
        const bookingsData = await response.json();
        setBookings(bookingsData);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
    }
  }, []);

  const fetchEligibilityData = useCallback(async () => {
    try {
      const [eligibilityResponse, typesResponse] = await Promise.all([
        fetch(API_ENDPOINTS.client.eligibility, {
          headers: getAuthHeaders(API_ENDPOINTS.client.eligibility),
          credentials: shouldIncludeCredentials(API_ENDPOINTS.client.eligibility) ? 'include' : 'same-origin'
        }),
        fetch(API_ENDPOINTS.business.appointmentTypes, {
          headers: getAuthHeaders(API_ENDPOINTS.business.appointmentTypes),
          credentials: shouldIncludeCredentials(API_ENDPOINTS.business.appointmentTypes) ? 'include' : 'same-origin'
        })
      ]);
      
      if (eligibilityResponse.ok) {
        const eligibilityData = await eligibilityResponse.json();
        setEligibility(eligibilityData.eligibility || []);
      }
      
      if (typesResponse.ok) {
        const typesData = await typesResponse.json();
        setAppointmentTypes(typesData.appointmentTypes || []);
      }
    } catch (err) {
      console.error('Error fetching eligibility data:', err);
    }
  }, []);

  // const handleCommentSubmit = async () => {
  //   if (clientData?.id) {
  //     await fetchComments(clientData.id);
  //   }
  // };

  // Handle sign out
  const handleSignOut = async () => {
    await logout();
  };

  // Check for tab parameter in URL
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'content' || tab === 'resources') {
      setActiveTab('resources');
    }
  }, [searchParams]);

  // Check authentication and fetch data
  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated || !client) {
        navigate('/client');
        return;
      }

      // Use client data from auth context
      if (client.intake_data) {
        setClientData(client.intake_data);
      }
      
      // Fetch additional data
      // if (client.id) {
      //   fetchComments(client.id);
      // }
      fetchBookings();
      fetchEligibilityData();
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, client, navigate, fetchBookings, fetchEligibilityData]);

  // const handleChange = (sectionPath: string, value: unknown) => {
  //   setClientData(prevData => {
  //     if (!prevData) return null;

  //     const keys = sectionPath.split('.');
  //     const updatedData = { ...prevData };
  //     let current: Record<string, unknown> = updatedData;

  //     for (let i = 0; i < keys.length - 1; i++) {
  //       current = current[keys[i]] as Record<string, unknown>;
  //     }

  //     current[keys[keys.length - 1]] = value;
  //     return updatedData;
  //   });
  // };

  // const handleUpdate = async () => {
  //   if (!clientData) return;

  //   try {
  //     const response = await fetch(`/api/update`, {
  //       method: 'PUT',
  //       headers: {
  //         'Content-Type': 'application/json'
  //       },
  //       credentials: 'include', // Include cookies for auth
  //       body: JSON.stringify(clientData),
  //     });

  //     if (!response.ok) {
  //       throw new Error('Failed to update client data');
  //     }

  //     setIsEditMode(false);
  //     setError(null);
  //   } catch (err) {
  //     setError(err instanceof Error ? err.message : 'Failed to update information');
  //   }
  // };

  const handleBookAppointment = async (appointmentTypeId: string) => {
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Include cookies for auth
        body: JSON.stringify({
          appointmentTypeId,
          successUrl: `${window.location.origin}/client/portal?tab=appointments`,
          cancelUrl: `${window.location.origin}/client/portal?tab=appointments`
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }
      
      const { checkoutUrl } = await response.json();
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to book appointment:', error);
      setError('Failed to start booking process. Please try again.');
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-stone-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-green-600 mx-auto mb-4"></div>
          <p className="text-brand-stone-600">Loading your information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-stone-100">
        <div className="bg-white shadow-lg rounded-xl p-8 text-center">
          <div className="text-red-600 mb-4">Error: {error}</div>
          <button
            onClick={() => navigate('/client')}
            className="bg-brand-green-600 text-white px-4 py-2 rounded-lg hover:bg-brand-green-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-stone-100">
        <div className="bg-white shadow-lg rounded-xl p-8 text-center">
          <div className="text-brand-stone-600 mb-4">No client data found.</div>
          <button
            onClick={() => navigate('/client')}
            className="bg-brand-green-600 text-white px-4 py-2 rounded-lg hover:bg-brand-green-700"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // const getNestedValue = (obj: object, path: string): unknown => {
  //   return path.split('.').reduce((acc: unknown, key: string) => (acc as Record<string, unknown>)?.[key], obj);
  // };

  // const formatAppointmentTime = (timeSlot: string): string => {
  //   return new Date(timeSlot).toLocaleString('en-US', {
  //     weekday: 'long',
  //     month: 'long', 
  //     day: 'numeric',
  //     hour: 'numeric',
  //     minute: '2-digit',
  //     hour12: true,
  //     timeZoneName: 'short'
  //   });
  // };

  // const formatCurrency = (cents: number, currency: string = 'USD'): string => {
  //   return new Intl.NumberFormat('en-US', {
  //     style: 'currency',
  //     currency: currency.toUpperCase()
  //   }).format(cents / 100);
  // };

  // const renderBookingCard = (booking: ClientBooking) => {
  //   const statusColors: Record<string, string> = {
  //     confirmed: 'bg-green-100 text-green-800 border-green-200',
  //     completed: 'bg-blue-100 text-blue-800 border-blue-200',
  //     cancelled: 'bg-red-100 text-red-800 border-red-200'
  //   };
    
  //   const statusColor = statusColors[booking.status] || 'bg-gray-100 text-gray-800 border-gray-200';
    
  //   return (
  //     <div key={booking.id} className={`border rounded-lg p-4 ${statusColor}`}>
  //       <div className="flex justify-between items-start mb-2">
  //         <h4 className="font-semibold text-brand-green-800">
  //           {booking.product_id.replace('appointment-', '').replace('-', ' ')}
  //         </h4>
  //         <span className="px-2 py-1 rounded-full text-xs font-medium bg-white">
  //           {booking.status}
  //         </span>
  //       </div>
  //       <p className="text-sm text-brand-stone-700 mb-2">
  //         {formatAppointmentTime(booking.time_slot)}
  //       </p>
  //       <div className="flex justify-between items-center text-sm">
  //         <span className="text-brand-stone-600">
  //           Amount: {formatCurrency(booking.amount, booking.currency)}
  //         </span>
  //         {booking.notes && (
  //           <span className="text-brand-stone-500 italic">
  //             Notes: {booking.notes}
  //           </span>
  //         )}
  //       </div>
  //     </div>
  //   );
  // };

  // Function to determine if intake form is completed
  const isIntakeCompleted = (data: IntakeFormData | null): boolean => {
    if (!data) return false;
    
    // Check if we have essential fields that indicate a completed intake
    const hasPersonalInfo = data.personalInfo?.fullName && data.personalInfo?.email;
    const hasHealthConcerns = data.healthConcerns?.mainConcern;
    // const hasClientStatement = data.clientStatement?.signature;
    
    // A form is considered complete if it has at least personal info and health concerns
    return Boolean(hasPersonalInfo && hasHealthConcerns);
  };

  return (
    <div className="min-h-screen bg-brand-stone-100">
      <div className="bg-white shadow-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-brand-green-800">
            Client Portal
          </h1>
          <button
            onClick={handleSignOut}
            className="bg-brand-stone-500 text-white px-4 py-2 rounded-lg hover:bg-brand-stone-600 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('appointments')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'appointments'
                  ? 'border-brand-green-500 text-brand-green-600'
                  : 'border-transparent text-brand-stone-500 hover:text-brand-stone-700 hover:border-brand-stone-300'
              }`}
            >
              Appointments
            </button>
            <button
              onClick={() => setActiveTab('resources')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'resources'
                  ? 'border-brand-green-500 text-brand-green-600'
                  : 'border-transparent text-brand-stone-500 hover:text-brand-stone-700 hover:border-brand-stone-300'
              }`}
            >
              Resources
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'appointments' && (
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Appointment Flow Overview */}
            {clientData && (
              <AppointmentFlow
                client={{
                  id: String(clientData.id || ''),
                  name: `${clientData.personalInfo?.fullName || ''}`,
                  email: clientData.personalInfo?.email || '',
                  intakeCompleted: isIntakeCompleted(clientData),
                  intakeCompletedDate: clientData.created_at
                }}
                eligibility={eligibility}
              />
            )}

            {/* Available Appointment Types */}
            <div className="bg-white shadow-lg rounded-xl p-6" id="appointment-types">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Available Appointment Types</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {appointmentTypes
                  .filter(type => type.active)
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map(type => {
                    const typeEligibility = eligibility.find(
                      e => e.appointmentTypeId === type.id
                    ) || { isEligible: false, reason: 'Loading...', appointmentTypeId: type.id };
                    
                    return (
                      <AppointmentTypeCard
                        key={type.id}
                        appointmentType={type}
                        eligibility={typeEligibility}
                        onBook={() => handleBookAppointment(type.id)}
                      />
                    );
                  })
                }
              </div>
            </div>

            {/* Current Appointments */}
            <div className="bg-white shadow-lg rounded-xl p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Appointments</h2>
              
              {(bookings.upcoming.length > 0 || bookings.past.length > 0 || bookings.cancelled.length > 0) ? (
                <div className="space-y-6">
                  {/* Upcoming Appointments */}
                  {bookings.upcoming.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-green-700 mb-4 flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Upcoming Appointments
                      </h3>
                      <div className="space-y-3">
                        {bookings.upcoming.map((booking) => (
                          <div key={booking.id} className="border border-green-200 rounded-lg p-4 bg-green-50">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-semibold text-green-800">
                                  Session: {booking.product_id}
                                </h4>
                                <p className="text-green-600">
                                  {new Date(booking.time_slot).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </p>
                                <p className="text-gray-600 text-sm">
                                  Amount: ${(booking.amount / 100).toFixed(2)} {booking.currency.toUpperCase()}
                                </p>
                              </div>
                              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                                {booking.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Past Appointments */}
                  {bookings.past.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                        Past Appointments
                      </h3>
                      <div className="space-y-3">
                        {bookings.past.map((booking) => (
                          <div key={booking.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-semibold text-gray-800">
                                  Session: {booking.product_id}
                                </h4>
                                <p className="text-gray-600">
                                  {new Date(booking.time_slot).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                              <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                                {booking.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center p-8 bg-gray-50 rounded-lg">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">
                    No Appointments Yet
                  </h3>
                  <p className="text-gray-500">
                    {isIntakeCompleted(clientData) 
                      ? "Ready to book your first appointment!" 
                      : "Complete your intake form to get started."}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'resources' && (
          <div className="bg-white shadow-lg rounded-xl p-6">
            <ClientContentLibrary />
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-red-600 text-sm">{error}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPortal;
